import type { DocCategory } from "../../views/documentCentre/categories";
import { baseApi } from "./baseApi";
import { NEVER_RETRIED } from "./noRetry";
import type { ChecklistItem } from "./sharepointApi";

/**
 * The Document Centre: one SharePoint folder per candidate.
 *
 * skyHire keeps a pointer from a person to their folder and never the files.
 * Everything inside a folder is read from SharePoint as the signed-in person,
 * so what comes back depends on who is asking: `DocumentFolderView.access` says
 * which case this caller is in, and only "ok" carries a folder or any file.
 *
 * A person is always addressed by one of their applications. Their email is the
 * key the server groups them by, and it never travels in a request or an
 * address.
 */

/**
 * One row per person, however many times they applied. Only people in a hired
 * stage are ever listed: documents are collected once someone is hired.
 */
export interface DocumentPerson {
  /** Their latest application: what every call and every link addresses them by. */
  applicationId: string;
  fullName: string | null;
  email: string | null;
  jobTitle: string | null;
  department: string | null;
  applicationStatus: string | null;
  /** When they were moved to a hired stage. */
  hiredAt: string | null;
  hasFolder: boolean;
  openRequests: number;
  /**
   * Sent something that nobody on the hiring team has opened yet, on ANY of
   * this person's hired applications, not only the one this row is for.
   */
  hasNewSubmission?: boolean;
  /**
   * The application that new submission is on: the one to open for it. It can
   * be an earlier hiring of the same person than `applicationId`. Null with
   * nothing new; absent from a server that predates it.
   */
  newSubmissionApplicationId?: string | null;
  /**
   * How many of the checklist's required documents this person has sent.
   * Counted by the server over everything, never from a page of a list. Null
   * when the organisation has no checklist.
   */
  checklist?: { done: number; total: number } | null;
}

export interface DocumentFolderLink {
  name: string;
  webUrl: string;
  linkedBy: string | null;
  linkedAt: string;
}

export interface DocumentFileItem {
  id: string;
  name: string;
  isFolder: boolean;
  size: number | null;
  mimeType: string | null;
  modifiedAt: string | null;
  modifiedBy: string | null;
  webUrl: string;
  /** What kind of document it is. Null for one put into SharePoint directly. */
  category?: DocCategory | null;
}

/**
 * ok               the caller can see the folder
 * not_linked       this person has no folder yet
 * not_signed_in    the caller has not signed in with Microsoft (or has to again)
 * no_access        a folder is linked, and SharePoint will not show it to this caller
 * site_not_allowed the folder is in a site that is no longer connected here
 * missing          the linked folder is gone from SharePoint. Only the person who
 *                  linked it, or an admin, is ever told this; everyone else gets
 *                  no_access, so nobody learns a folder existed from its absence.
 */
export type DocumentFolderAccess =
  | "ok"
  | "not_linked"
  | "not_signed_in"
  | "no_access"
  | "site_not_allowed"
  | "missing";

export interface DocumentFolderSuggestion {
  /** The skyHire id of the site it is in. Goes back with the driveId when it is linked. */
  siteId: string;
  driveId: string;
  itemId: string;
  name: string;
  path: string;
}

export interface DocumentFolderView {
  person: DocumentPerson;
  access: DocumentFolderAccess;
  /** Null unless access is "ok". */
  folder: DocumentFolderLink | null;
  /** Sent for "no_access", so the screen can say who to ask. */
  linkedBy: string | null;
  breadcrumb: { label: string; itemId: string }[];
  /** Empty unless access is "ok". */
  files: DocumentFileItem[];
  /** The server stopped reading before the end: more files exist than `files` shows. */
  truncated: boolean;
  /** A folder already under the default location whose name matches this person. */
  suggested: DocumentFolderSuggestion | null;
  /**
   * Another application of the same person that already has a folder, when
   * this one is hired and has none and the caller may see the other. The
   * folder kept for that job can be used for this one too.
   */
  sibling?: { applicationId: string; jobTitle: string | null } | null;
  /**
   * The organisation's checklist as it stands for this person: which of its
   * documents have come in, and how many of the required ones. Null when no
   * checklist is set.
   */
  checklist?: {
    done: number;
    total: number;
    /**
     * `received`: a file arrived for it and has not been sent back.
     * `resendAsked`: a review sent it back and no new file has come yet;
     * `received` is then false. Absent from a server that predates it, which
     * means no.
     */
    items: (ChecklistItem & { received: boolean; resendAsked?: boolean })[];
  } | null;
  canEdit: boolean;
  /**
   * False for someone who is not in a hired stage (reached by address, or
   * moved out of one after a folder was made). What is there can be read;
   * nothing new can be added, and the server refuses it if tried.
   */
  isHired?: boolean;
}

/** One thing a request asks for, how many files have come in for it, and what was made of them. */
export interface DocumentRequestItem {
  id: string;
  label: string;
  received: number;
  category?: DocCategory;
  /** The checklist document this is, when it was asked for from the checklist. */
  checklistItemId?: string | null;
  /** The hiring team's word on it at the last review. Null until it has been reviewed. */
  verdict?: "accepted" | "resend" | null;
  /** Why it was asked for again. */
  reason?: string | null;
  /**
   * Whether the candidate has to send it, or may. Absent on an answer from a
   * server that predates the choice, which means required: every request
   * sent before there was one asked for all of its documents.
   */
  required?: boolean;
}

/**
 * Something to ask a candidate for. A plain label is a document of no
 * particular kind, which is what every request sent before there were kinds.
 */
export type NewRequestItem =
  | string
  | {
      label: string;
      category?: DocCategory;
      checklistItemId?: string;
      /** Left out, the server decides (the checklist's own word, or required). */
      required?: boolean;
    };

/**
 * waiting            sent, and the candidate has not submitted yet
 * submitted          the candidate pressed Submit; the hiring team has to look
 * changes_requested  reviewed, and something was asked for again
 * completed          reviewed, and everything was accepted
 * cancelled          withdrawn by the hiring team
 */
export type DocumentRequestState =
  | "waiting"
  | "submitted"
  | "changes_requested"
  | "completed"
  | "cancelled";

/** What the caller may do with a request now. The server decides; the screen only draws them. */
export type DocumentRequestAction = "remind" | "review" | "cancel" | "take_over";

/** A file the candidate sent, as the reviewer is allowed to see it in SharePoint. */
export interface DocumentReviewFile {
  itemId: string;
  name: string;
  size: number | null;
  createdAt: string;
}

/** What a request holds, for the person reviewing it. Read from SharePoint as the caller. */
export interface DocumentRequestReview {
  items: {
    id: string;
    label: string;
    files: DocumentReviewFile[];
    verdict: "accepted" | "resend" | null;
    reason: string | null;
    /** Whether the candidate had to send it. Absent means required. */
    required?: boolean;
    /**
     * The server's word on whether a review has to rule on it: not for an
     * optional document nothing was sent for and nobody has asked for again.
     * Decided there and only there: `files` holds what this reviewer can
     * open, which may be fewer than arrived. Absent from a server that
     * predates optional documents, where everything needs a verdict.
     */
    needsVerdict?: boolean;
  }[];
  /** Files sent for none of the items. */
  other: DocumentReviewFile[];
  /** Files that arrived and that this person cannot see in SharePoint. They are not listed. */
  unreadable: number;
  /**
   * Names the state of the review as it was read. It goes back unchanged with
   * the decisions, so the server can tell that someone else reviewed in
   * between and refuse instead of overwriting them. Empty before any review.
   */
  version: string;
}

export interface DocumentReviewVerdict {
  id: string;
  verdict: "accepted" | "resend";
  reason?: string;
}

export interface DocumentRequest {
  id: string;
  items: DocumentRequestItem[];
  /** When the candidate pressed Submit on their upload page. Null until they have. */
  submittedAt: string | null;
  /** Submitted, and not yet opened by anyone on the hiring team. */
  isNew?: boolean;
  /**
   * The candidate's agreement to share their documents: when, and the wording
   * they saw (null for one given before wording was kept). Null until they agree.
   */
  consent?: { at: string; text: string | null } | null;
  note: string | null;
  dueDate: string | null;
  /**
   * The due date has passed, by the server's one rule for it, and the
   * candidate can no longer upload. The request itself is still open: a reminder
   * with a new due date opens the link again. Absent from a server that
   * predates it, which means no.
   */
  pastDue?: boolean;
  status: "open" | "closed" | "cancelled";
  state: DocumentRequestState;
  actions: DocumentRequestAction[];
  /** Who accepted everything, and when. Null until a request is completed. */
  review?: { by: string; at: string } | null;
  /** Null once the person who sent it has been removed, which is when `needsOwner` is true. */
  requestedBy: string | null;
  createdAt: string;
  expiresAt: string;
  reminderCount: number;
  /**
   * How many files came in, counting the ones sent for none of the items.
   * Their names are shown in the folder and nowhere else.
   */
  uploadCount: number;
  /**
   * The person who sent it has left. Uploads go in under the sender's Microsoft
   * sign-in, so the link is dead until someone takes the request over.
   */
  needsOwner: boolean;
}

export interface DocumentAuditEntry {
  id: string;
  action: string;
  /** Null when the candidate did it through their upload link. */
  actor: string | null;
  createdAt: string;
  /**
   * On an action that emails, whether the email went out. The one thing of a
   * row's detail that is sent: a yes or no that names nothing, since this
   * list is shown to people SharePoint may not let into the folder. Absent on
   * other actions and from a server that predates it.
   */
  emailed?: boolean;
  /**
   * On a reminder or a review that changed the request's due date: the new
   * day ("YYYY-MM-DD"), or null when the date was taken off. Absent when the
   * date did not change, on every other action, and from an older server.
   */
  dueDate?: string | null;
}

/**
 * folders  hired people with a folder or an open request, newest activity first
 * missing  hired people with no folder yet, most recently hired first
 */
export type DocumentPeopleView = "folders" | "missing";

export interface DocumentPeopleArgs {
  instanceId: string;
  view?: DocumentPeopleView;
  q?: string;
  jobId?: string;
  stage?: string;
  limit?: number;
  offset?: number;
}

export interface DocumentPeoplePage {
  people: DocumentPerson[];
  total: number;
  /** How many people each view holds in all, whatever is being searched for. */
  /** `missingDocuments` is absent from a server that predates that list. */
  counts?: { folders: number; missing: number; missingDocuments?: number };
}

export interface LinkFolderArgs {
  instanceId: string;
  applicationId: string;
  /**
   * create makes a new folder; link points at one that already exists;
   * sibling uses the folder another application of the same person has.
   */
  mode: "create" | "link" | "sibling";
  /** For sibling: the application whose folder is to be used. */
  siblingApplicationId?: string;
  /** The skyHire id of the chosen site. Goes with every driveId. */
  siteId?: string;
  /** For create: another parent than the default location. For link: the folder itself. */
  driveId?: string;
  itemId?: string;
  /** Overrides the name the server would give a new folder. */
  name?: string;
}

interface ApplicationArgs {
  instanceId: string;
  applicationId: string;
}

/** What starting an upload returns: where to send the bytes, and in what size pieces. */
export interface DocumentUploadTicket {
  uploadId: string;
  chunkSize: number;
}

/** One open request that still has documents outstanding. */
export interface MissingDocumentsRow {
  requestId: string;
  applicationId: string;
  fullName: string | null;
  jobTitle: string | null;
  missing: { id: string; label: string; category: DocCategory }[];
  requestedAt: string;
  /** YYYY-MM-DD. */
  dueDate: string | null;
  /** The server's word on it, so it means the same for every viewer. */
  overdue: boolean;
  /** The same fact under the name a request gives it: the candidate can no longer upload. */
  pastDue?: boolean;
  lastRemindedAt: string | null;
  state: "waiting" | "submitted" | "changes_requested";
  /** What this viewer may do with the request, as on a request itself. */
  actions: string[];
}

export interface MissingDocumentsPage {
  rows: MissingDocumentsRow[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * What a bulk action came back with: one answer per thing sent in, in `id`
 * (the application or the request that was named). `message` is the sentence
 * to show for one that did not go through.
 */
export interface BulkResult {
  results: (
    | { id: string; ok: true; requestId: string }
    | { id: string; ok: false; code: string; message: string }
  )[];
  sent: number;
  failed: number;
}

/** Leaves out what was not asked for, so the server never reads an empty filter as a real one. */
function definedParams(params: Record<string, string | number | boolean | undefined | null>) {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") out[key] = value;
  }
  return out;
}

/**
 * A due date closes the candidate's link at midnight after that day in the
 * sender's own timezone, so a body that sets one says which timezone that is.
 * Without it the server falls back to its older, later closing rule.
 */
export function withSenderTimeZone<T extends { dueDate?: string | null }>(
  body: T,
): T & { timeZone?: string } {
  if (typeof body.dueDate !== "string" || body.dueDate === "") return body;
  try {
    return { ...body, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone };
  } catch {
    return body;
  }
}

export const candidateDocumentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /** Paged on the server, and makes no SharePoint call: it is the same for every caller. */
    getDocumentPeople: builder.query<DocumentPeoplePage, DocumentPeopleArgs>({
      query: ({ instanceId, view, q, jobId, stage, limit, offset }) => ({
        url: "/candidate-documents/people",
        method: "GET",
        params: definedParams({ instanceId, view, q, jobId, stage, limit, offset }),
      }),
      providesTags: ["CandidateDocumentPeople"],
    }),

    /**
     * Open requests with documents still outstanding, paged by the server.
     * Tagged with the people list: whatever changes that changes this.
     */
    getMissingDocuments: builder.query<
      MissingDocumentsPage,
      { instanceId: string; search?: string; jobId?: string; page: number; pageSize: number }
    >({
      query: ({ instanceId, search, jobId, page, pageSize }) => ({
        url: "/candidate-documents/missing-documents",
        method: "GET",
        params: definedParams({ instanceId, search, jobId, page, pageSize }),
      }),
      providesTags: ["CandidateDocumentPeople"],
    }),

    /**
     * How many candidates have a submission nobody has opened, for the badge
     * on the sidebar item. Polled, so it is NOT retried by the layer as other
     * reads are: the next poll is its retry, and a server that is down must
     * get one request per interval, not four. Tagged with the people list:
     * marking a folder seen refreshes both.
     */
    getNewSubmissionsCount: builder.query<{ count: number }, { instanceId: string }>({
      extraOptions: NEVER_RETRIED,
      query: ({ instanceId }) => ({
        url: "/candidate-documents/new-submissions/count",
        method: "GET",
        params: { instanceId },
      }),
      providesTags: ["CandidateDocumentPeople"],
    }),

    /**
     * Says that this person's new submissions have been looked at. Afterwards
     * the people list, the count and their requests are read again, so the
     * marks go without a reload.
     */
    markCandidateFolderSeen: builder.mutation<unknown, ApplicationArgs>({
      extraOptions: NEVER_RETRIED,
      query: (body) => ({ url: "/candidate-documents/folder/seen", method: "POST", body }),
      invalidatesTags: (_result, _error, arg) => [
        "CandidateDocumentPeople",
        { type: "CandidateDocumentRequests", id: arg.applicationId },
      ],
    }),

    /** `itemId` opens a subfolder of the person's folder; without it, the folder itself. */
    getCandidateFolder: builder.query<DocumentFolderView, ApplicationArgs & { itemId?: string }>({
      query: ({ instanceId, applicationId, itemId }) => ({
        url: "/candidate-documents/folder",
        method: "GET",
        params: definedParams({ instanceId, applicationId, itemId }),
      }),
      providesTags: (_result, _error, arg) => [
        { type: "CandidateDocumentFolder", id: arg.applicationId },
      ],
    }),

    /**
     * Every cached folder view is dropped, not just this application's: the
     * folder belongs to the person, and someone who applied twice has two
     * applications showing the same one.
     */
    linkCandidateFolder: builder.mutation<DocumentFolderLink, LinkFolderArgs>({
      extraOptions: NEVER_RETRIED,
      query: (body) => ({ url: "/candidate-documents/folder", method: "POST", body }),
      invalidatesTags: (_result, _error, arg) => [
        "CandidateDocumentPeople",
        "SharePointBrowse",
        "CandidateDocumentFolder",
        { type: "CandidateDocumentAudit", id: arg.applicationId },
      ],
    }),

    /** Unlinks only. Nothing is ever deleted in SharePoint from here. */
    unlinkCandidateFolder: builder.mutation<{ success: boolean }, ApplicationArgs>({
      extraOptions: NEVER_RETRIED,
      query: ({ instanceId, applicationId }) => ({
        url: "/candidate-documents/folder",
        method: "DELETE",
        params: { instanceId, applicationId },
      }),
      invalidatesTags: (_result, _error, arg) => [
        "CandidateDocumentPeople",
        "CandidateDocumentFolder",
        { type: "CandidateDocumentAudit", id: arg.applicationId },
      ],
    }),

    /**
     * A short-lived link to one file, to be opened at once and never kept. A
     * mutation although it is a GET: a cached link must never be handed out,
     * and every open is recorded, so the activity list refreshes after it.
     */
    getCandidateFileUrl: builder.mutation<
      { url: string },
      ApplicationArgs & { itemId: string; mode: "preview" | "download" }
    >({
      extraOptions: NEVER_RETRIED,
      query: ({ instanceId, applicationId, itemId, mode }) => ({
        url: "/candidate-documents/file-url",
        method: "GET",
        params: { instanceId, applicationId, itemId, mode },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "CandidateDocumentAudit", id: arg.applicationId },
      ],
    }),

    /**
     * Starts an upload into the person's folder. The bytes then go to skyHire's
     * own server in pieces of `chunkSize` (views/documentCentre/upload), which
     * passes them on to SharePoint as the caller. `category` and
     * `checklistItemId` say what the file is: one answer for a whole batch.
     */
    startCandidateUpload: builder.mutation<
      DocumentUploadTicket,
      ApplicationArgs & {
        fileName: string;
        size: number;
        parentItemId?: string;
        category?: DocCategory;
        checklistItemId?: string;
      }
    >({
      extraOptions: NEVER_RETRIED,
      query: (body) => ({ url: "/candidate-documents/uploads", method: "POST", body }),
    }),

    /** Asks whoever linked the folder to share it in SharePoint. */
    requestFolderAccess: builder.mutation<
      { emailed: boolean },
      ApplicationArgs & { message?: string }
    >({
      extraOptions: NEVER_RETRIED,
      query: (body) => ({ url: "/candidate-documents/access-request", method: "POST", body }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "CandidateDocumentAudit", id: arg.applicationId },
      ],
    }),

    getDocumentRequests: builder.query<{ requests: DocumentRequest[] }, ApplicationArgs>({
      query: ({ instanceId, applicationId }) => ({
        url: "/candidate-documents/requests",
        method: "GET",
        params: { instanceId, applicationId },
      }),
      providesTags: (_result, _error, arg) => [
        { type: "CandidateDocumentRequests", id: arg.applicationId },
      ],
    }),

    /**
     * Sends the candidate an upload link. `emailed: false` = recorded, but no
     * email went out. The server gives each item an id.
     */
    createDocumentRequest: builder.mutation<
      { id: string; emailed: boolean },
      ApplicationArgs & { items: NewRequestItem[]; note?: string; dueDate?: string }
    >({
      extraOptions: NEVER_RETRIED,
      query: (body) => ({
        url: "/candidate-documents/requests",
        method: "POST",
        body: withSenderTimeZone(body),
      }),
      invalidatesTags: (_result, _error, arg) => [
        "CandidateDocumentPeople",
        { type: "CandidateDocumentRequests", id: arg.applicationId },
        { type: "CandidateDocumentAudit", id: arg.applicationId },
      ],
    }),

    /**
     * Emails a fresh link. The one sent before stops working. Refused with
     * RECIPIENT_CHANGED, and nothing sent, when the candidate's email is no
     * longer the one the link last went to: the same call with
     * `confirmRecipient` sends it to the new address.
     */
    remindDocumentRequest: builder.mutation<
      { emailed: boolean },
      ApplicationArgs & {
        id: string;
        confirmRecipient?: boolean;
        /**
         * A new due date with the reminder, or null to remove it. Left out,
         * the date stays as it is. Required by the server when the old one
         * has passed: a reminder would otherwise email a link that takes no files.
         */
        dueDate?: string | null;
      }
    >({
      extraOptions: NEVER_RETRIED,
      query: ({ instanceId, id, confirmRecipient, dueDate }) => ({
        url: `/candidate-documents/requests/${encodeURIComponent(id)}/remind`,
        method: "POST",
        body: withSenderTimeZone({
          instanceId,
          ...(confirmRecipient ? { confirmRecipient: true } : {}),
          // The key itself is the message: absent is "leave it", null is "none".
          ...(dueDate === undefined ? {} : { dueDate }),
        }),
      }),
      invalidatesTags: (_result, _error, arg) => [
        // The list of what is outstanding says when each was last reminded.
        "CandidateDocumentPeople",
        { type: "CandidateDocumentRequests", id: arg.applicationId },
        { type: "CandidateDocumentAudit", id: arg.applicationId },
      ],
    }),

    /**
     * Asks up to 50 people for the same documents in one call. Each is
     * answered on its own: someone with no folder and nowhere to make one
     * comes back as not sent with the reason, and the rest still go.
     */
    createDocumentRequestsBulk: builder.mutation<
      BulkResult,
      {
        instanceId: string;
        applicationIds: string[];
        items: NewRequestItem[];
        note?: string;
        dueDate?: string;
      }
    >({
      extraOptions: NEVER_RETRIED,
      query: (body) => ({
        url: "/candidate-documents/requests/bulk",
        method: "POST",
        body: withSenderTimeZone(body),
      }),
      // Whichever candidates these were, their folders, requests and history changed.
      invalidatesTags: [
        "CandidateDocumentPeople",
        "CandidateDocumentFolder",
        "CandidateDocumentRequests",
        "CandidateDocumentAudit",
      ],
    }),

    /**
     * Reminds up to 50 requests in one call. Each is answered on its own: a
     * candidate whose address changed is never reminded in bulk, and comes
     * back as not sent with the reason.
     */
    remindDocumentRequestsBulk: builder.mutation<
      BulkResult,
      { instanceId: string; requestIds: string[]; dueDate?: string | null }
    >({
      extraOptions: NEVER_RETRIED,
      query: (body) => ({
        url: "/candidate-documents/reminders/bulk",
        method: "POST",
        body: withSenderTimeZone(body),
      }),
      // Whichever candidates these were, their requests and history changed.
      invalidatesTags: [
        "CandidateDocumentPeople",
        "CandidateDocumentRequests",
        "CandidateDocumentAudit",
      ],
    }),

    /** The candidate's link stops working at once. */
    cancelDocumentRequest: builder.mutation<{ success: boolean }, ApplicationArgs & { id: string }>(
      {
        extraOptions: NEVER_RETRIED,
        query: ({ instanceId, id }) => ({
          url: `/candidate-documents/requests/${encodeURIComponent(id)}/cancel`,
          method: "POST",
          body: { instanceId },
        }),
        invalidatesTags: (_result, _error, arg) => [
          "CandidateDocumentPeople",
          { type: "CandidateDocumentRequests", id: arg.applicationId },
          { type: "CandidateDocumentAudit", id: arg.applicationId },
        ],
      },
    ),

    /**
     * What the candidate sent for a request, file by file, for the person
     * reviewing it. Never cached across openings: it is read from SharePoint
     * as the caller, and what they may see can change.
     */
    getRequestReview: builder.query<DocumentRequestReview, ApplicationArgs & { id: string }>({
      query: ({ instanceId, id }) => ({
        url: `/candidate-documents/requests/${encodeURIComponent(id)}/review`,
        method: "GET",
        params: { instanceId },
      }),
      keepUnusedDataFor: 0,
    }),

    /**
     * Records the review: every item accepted completes the request, anything
     * to send again goes back to the candidate with the reasons. This is the
     * only way a request is completed.
     */
    reviewDocumentRequest: builder.mutation<
      { state: "completed" | "changes_requested"; emailed: boolean },
      ApplicationArgs & {
        id: string;
        version: string;
        items: DocumentReviewVerdict[];
        /** Send a send-back to an address that has changed since the link last went out. */
        confirmRecipient?: boolean;
        /**
         * A new due date with a send-back, or null to remove it; left out, it
         * stays. Required by the server when the old one has passed and
         * something is being asked for again.
         */
        dueDate?: string | null;
      }
    >({
      extraOptions: NEVER_RETRIED,
      query: ({ instanceId, id, version, items, confirmRecipient, dueDate }) => ({
        url: `/candidate-documents/requests/${encodeURIComponent(id)}/review`,
        method: "POST",
        body: withSenderTimeZone({
          instanceId,
          version,
          items,
          ...(confirmRecipient ? { confirmRecipient: true } : {}),
          ...(dueDate === undefined ? {} : { dueDate }),
        }),
      }),
      invalidatesTags: (_result, _error, arg) => [
        "CandidateDocumentPeople",
        // The folder's checklist says what has been received, and a document
        // sent back is no longer that.
        { type: "CandidateDocumentFolder", id: arg.applicationId },
        { type: "CandidateDocumentRequests", id: arg.applicationId },
        { type: "CandidateDocumentAudit", id: arg.applicationId },
      ],
    }),

    /** Makes the caller the owner of a request whose sender has left, so its uploads work again. */
    takeOverDocumentRequest: builder.mutation<
      { success: boolean },
      ApplicationArgs & { id: string }
    >({
      extraOptions: NEVER_RETRIED,
      query: ({ instanceId, id }) => ({
        url: `/candidate-documents/requests/${encodeURIComponent(id)}/take-over`,
        method: "POST",
        body: { instanceId },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "CandidateDocumentRequests", id: arg.applicationId },
        { type: "CandidateDocumentAudit", id: arg.applicationId },
      ],
    }),

    /**
     * How many of these applications have anything in the Document Centre: a
     * folder pointer, a request or an upload record. For the "delete
     * applicant" confirmation. Asked afresh each time it opens.
     */
    getLinkedDocuments: builder.query<
      { count: number },
      { instanceId: string; applicationIds: string[] }
    >({
      query: ({ instanceId, applicationIds }) => ({
        url: "/candidate-documents/linked",
        method: "GET",
        params: { instanceId, applicationIds: applicationIds.join(",") },
      }),
      keepUnusedDataFor: 0,
    }),

    getDocumentAudit: builder.query<
      { entries: DocumentAuditEntry[] },
      ApplicationArgs & { limit?: number }
    >({
      query: ({ instanceId, applicationId, limit }) => ({
        url: "/candidate-documents/audit",
        method: "GET",
        params: definedParams({ instanceId, applicationId, limit }),
      }),
      providesTags: (_result, _error, arg) => [
        { type: "CandidateDocumentAudit", id: arg.applicationId },
      ],
    }),
  }),
});

export const {
  useGetDocumentPeopleQuery,
  useMarkCandidateFolderSeenMutation,
  useGetMissingDocumentsQuery,
  useGetCandidateFolderQuery,
  useLinkCandidateFolderMutation,
  useUnlinkCandidateFolderMutation,
  useGetCandidateFileUrlMutation,
  useStartCandidateUploadMutation,
  useRequestFolderAccessMutation,
  useGetDocumentRequestsQuery,
  useCreateDocumentRequestMutation,
  useRemindDocumentRequestMutation,
  useRemindDocumentRequestsBulkMutation,
  useCreateDocumentRequestsBulkMutation,
  useCancelDocumentRequestMutation,
  useGetRequestReviewQuery,
  useReviewDocumentRequestMutation,
  useTakeOverDocumentRequestMutation,
  useGetDocumentAuditQuery,
} = candidateDocumentsApi;
